import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Format relatif "il y a 3 min" / "3 min ago", auto-rafraîchi.
 * - < 5 s    → "just now" / "à l'instant"
 * - < 60 s   → "N s ago" / "il y a N s"
 * - < 60 min → "N min ago" / "il y a N min"
 * - < 24 h   → "N h ago" / "il y a N h"
 * - sinon    → date courte localisée via toLocaleString
 *
 * `<RelativeTime iso="..."/>` — l'ISO complet est en `title` (hover natif).
 * Refresh interne : 1 fois / 30 s. Suffit pour la perception "fresh".
 *
 * `formatRelative` reste exportée pour les tests unitaires (signature
 * pure, pas de dépendance React). Elle accepte un `t` optionnel pour
 * lookup i18n ; à défaut elle utilise les chaînes EN par défaut. Le
 * composant React câble `useTranslation()` → `t`.
 */

const DEFAULT_STRINGS_EN = {
  just_now: "just now",
  seconds_ago: (n) => `${n}s ago`,
  minutes_ago: (n) => `${n} min ago`,
  hours_ago: (n) => `${n}h ago`,
};

function localeFor(lng) {
  return lng && lng.startsWith("fr") ? "fr-FR" : "en-US";
}

export function formatRelative(isoOrDate, opts = {}) {
  const { now = new Date(), t = null, locale = "en" } = opts;
  if (!isoOrDate) return "";
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  const diffSec = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 1000));
  if (diffSec < 5) {
    return t ? t("relative_time.just_now") : DEFAULT_STRINGS_EN.just_now;
  }
  if (diffSec < 60) {
    return t
      ? t("relative_time.seconds_ago", { n: diffSec })
      : DEFAULT_STRINGS_EN.seconds_ago(diffSec);
  }
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return t
      ? t("relative_time.minutes_ago", { n: diffMin })
      : DEFAULT_STRINGS_EN.minutes_ago(diffMin);
  }
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) {
    return t
      ? t("relative_time.hours_ago", { n: diffH })
      : DEFAULT_STRINGS_EN.hours_ago(diffH);
  }
  // > 24 h : on bascule en absolu court. toLocaleString prend en
  // charge fr-FR / en-US nativement — pas de string à traduire ici.
  return d.toLocaleString(localeFor(locale), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function RelativeTime({ iso, className, style }) {
  const { t, i18n } = useTranslation("common");
  // Tick toutes les 30 s pour rafraîchir le label sans abuser du re-render.
  const [, force] = useState(0);
  useEffect(() => {
    const ti = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(ti);
  }, []);

  if (!iso) return null;

  const exact = new Date(iso).toLocaleString(localeFor(i18n.language), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return (
    <time dateTime={iso} title={exact} className={className} style={style}>
      {formatRelative(iso, { t, locale: i18n.language })}
    </time>
  );
}
